import { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  Slider,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import { PhoneFrame, TabletFrame } from './HtmlPreviewFrames';

type DeviceMode = 'mobile_responsive' | 'mobile_full' | 'tablet' | 'desktop' | 'custom';

interface DeviceOption {
  key: DeviceMode;
  label: string;
  subtitle: string | null;
  width: number | null;
}

const DEVICES: DeviceOption[] = [
  { key: 'mobile_responsive', label: 'MOBILE RESPONSIVE', subtitle: "Jusqu'à 320px de large", width: 320 },
  { key: 'mobile_full', label: 'MOBILE PLEINE LARGEUR', subtitle: "Jusqu'à 320px de large", width: 375 },
  { key: 'tablet', label: 'TABLETTE', subtitle: "Jusqu'à 768px de large", width: 768 },
  { key: 'desktop', label: 'ORDINATEUR', subtitle: 'Plus large que 800px', width: 900 },
  { key: 'custom', label: 'PERSONNALISÉ', subtitle: null, width: null },
];

interface HtmlPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  htmlContent: string;
  defaultDevice?: DeviceMode;
}

function HtmlPreviewDialog({ open, onClose, htmlContent, defaultDevice = 'mobile_responsive' }: HtmlPreviewDialogProps) {
  const { sidebarColor } = useLayoutPreferences();
  const [activeDevice, setActiveDevice] = useState<DeviceMode>(defaultDevice);
  const [customWidth, setCustomWidth] = useState(1020);

  const currentDevice = DEVICES.find(d => d.key === activeDevice)!;
  const previewWidth = currentDevice.width ?? customWidth;

  const isMobile = activeDevice === 'mobile_responsive' || activeDevice === 'mobile_full';
  const isTablet = activeDevice === 'tablet';

  const iframe = (
    <iframe
      srcDoc={htmlContent || '<p style="color:#aaa;padding:16px;">Aucun contenu HTML</p>'}
      style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      title="Aperçu email"
    />
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { borderRadius: 0, backgroundColor: '#f4f5f7' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          backgroundColor: '#fff',
          borderBottom: '1px solid #dee2e6',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
          Aperçu
        </Typography>
        <Button
          variant="text"
          onClick={onClose}
          sx={{ textTransform: 'none', color: '#6c757d', fontWeight: 500 }}
        >
          Fermer l'aperçu ✕
        </Button>
      </Box>

      <Box sx={{ display: 'flex', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
        <Box
          sx={{
            width: 180,
            minWidth: 180,
            backgroundColor: '#fff',
            borderRight: '1px solid #dee2e6',
            overflowY: 'auto',
          }}
        >
          <List disablePadding>
            {DEVICES.map(device => (
              <ListItemButton
                key={device.key}
                selected={activeDevice === device.key}
                onClick={() => setActiveDevice(device.key)}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid #f0f0f0',
                  '&.Mui-selected': {
                    backgroundColor: sidebarColor,
                    color: '#fff',
                    '&:hover': { backgroundColor: sidebarColor },
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        fontSize: '0.68rem',
                        letterSpacing: 0.3,
                        color: activeDevice === device.key ? '#fff' : '#495057',
                      }}
                    >
                      {device.label}
                    </Typography>
                  }
                  secondary={
                    device.key !== 'custom' && device.subtitle ? (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          color: activeDevice === device.key ? 'rgba(255,255,255,0.8)' : '#adb5bd',
                          fontStyle: 'italic',
                        }}
                      >
                        {device.subtitle}
                      </Typography>
                    ) : null
                  }
                  disableTypography
                />
              </ListItemButton>
            ))}
          </List>

          {activeDevice === 'custom' && (
            <Box sx={{ px: 2, pt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                Taille de l'écran : {customWidth} px
              </Typography>
              <Slider
                value={customWidth}
                min={320}
                max={1600}
                step={10}
                onChange={(_, val) => setCustomWidth(val as number)}
                size="small"
                sx={{ color: sidebarColor }}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 3,
            px: 2,
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: sidebarColor,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { backgroundColor: sidebarColor, opacity: 0.9 },
            }}
          >
            Revenir à la page d'origine
          </Button>

          {isMobile ? (
            <PhoneFrame width={previewWidth}>{iframe}</PhoneFrame>
          ) : isTablet ? (
            <TabletFrame width={previewWidth}>{iframe}</TabletFrame>
          ) : (
            <Box
              sx={{
                width: previewWidth,
                maxWidth: '100%',
                minHeight: 600,
                border: '1px solid #dee2e6',
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ height: 600 }}>{iframe}</Box>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default HtmlPreviewDialog;
